import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { NetworkONTResponseTypes } from "../common/response.common";
import type { SelectHook, SelectParam } from "../common/types.common";
import { asUpdateHook, buildSelectParam, get, post, postEmpty } from "./common";

export const networkONT = {
    record(data: typeof Requests.NetworkONT.Record.Input) {
        return post<typeof data, NetworkONTResponseTypes.Record>(`/network-ont/record`, data);
    },
    obtain(id: string) {
        return get<NetworkONTResponseTypes.Obtain>(`/network-ont/obtain/${id}`);
    },
    dump(id: string) {
        return postEmpty(`/network-ont/dump/${id}`);
    },
    refresh(id: string) {
        return postEmpty(`/network-ont/refresh/${id}`);
    },
    createWAN(id: string, data: typeof Requests.NetworkONT.CreateWAN.Input) {
        return post<typeof data>(`/network-ont/create-wan/${id}`, data);
    },
    deleteWAN(id: string, data: typeof Requests.NetworkONT.DeleteWAN.Input) {
        return post<typeof data>(`/network-ont/delete-wan/${id}`, data);
    },
    updateWAN(id: string, data: typeof Requests.NetworkONT.UpdateWAN.Input) {
        return post<typeof data>(`/network-ont/update-wan/${id}`, data);
    },
    updateLAN(id: string, data: typeof Requests.NetworkONT.UpdateLAN.Input) {
        return post<typeof data>(`/network-ont/update-lan/${id}`, data);
    },
    updateWiFi(id: string, data: typeof Requests.NetworkONT.UpdateWiFi.Input) {
        return post<typeof data>(`/network-ont/update-wifi/${id}`, data);
    },
    createSubscriptionWAN(
        id: string,
        data: typeof Requests.NetworkONT.CreateSubscriptionWAN.Input,
    ) {
        return post<typeof data>(`/network-ont/create-subscription-wan/${id}`, data);
    },
    attachSubscriptions(id: string) {
        return postEmpty(`/network-ont/attach-subscriptions/${id}`);
    },
    updateSystem(id: string, data: typeof Requests.NetworkONT.UpdateSystem.Input) {
        return post<typeof data>(`/network-ont/update-system/${id}`, data);
    },
    reboot(id: string) {
        return postEmpty(`/network-ont/reboot/${id}`);
    },
    delete(id: string) {
        return postEmpty(`/network-ont/delete/${id}`);
    },
    generateACSURL() {
        return get<NetworkONTResponseTypes.GenerateACSURL>(`/network-ont/generate-acs-url`);
    },
    formHooks: {
        select: (async (param?: SelectParam) => {
            const req = get<NetworkONTResponseTypes.Select>(
                `/network-ont/select` + buildSelectParam(param),
            );

            return (await req.execute()) ?? [];
        }) satisfies SelectHook,
        update(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.NetworkONT.Update.Input) => {
                return await asUpdateHook(
                    post<typeof data>(`/network-ont/update/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
